/**
 * project-manager.js
 * Quản lý Dự Án Voxel Anime Studio (.vas.json).
 * Hỗ trợ lưu trữ trực tiếp vào máy tính qua File System Access API (showSaveFilePicker, showOpenFilePicker)
 * và fallback sang tải xuống / tải lên file chuẩn trình duyệt.
 */

const DRAFT_STORAGE_KEY = 'VAS_PROJECT_DRAFT';

export class ProjectManager {
  constructor() {
    this.currentProject = this.createDefaultProject();
    this.fileHandle = null;
  }

  /**
   * Khởi tạo dự án mẫu mặc định
   * @returns {object} VoxelAnimeProject
   */
  createDefaultProject() {
    return {
      version: '1.0',
      title: 'Anime Phố Phường - Hoạt Cảnh Mẫu',
      author: 'Đạo diễn Voxel',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      environment: {
        timeOfDay: 'noon', // 'noon' | 'sunset' | 'night'
        sceneType: 'city', // 'city' | 'studio'
        isTrafficEnabled: true,
        backgroundMusic: 'none'
      },
      characters: [], // Danh sách nhân vật cast vào dự án
      storyboard: [
        {
          id: 'shot_1',
          title: 'Phân cảnh 1: Chào hỏi quán nước',
          duration: 4.0,
          actorId: 'char_teacher',
          actorName: 'Cô Giáo Lan',
          spawnPoint: 'sidewalk_center', // 'sidewalk_center' | 'tea_stall_bench' | 'street_cross'
          action: 'wave',
          expression: 'happy',
          emotionFx: 'none',
          dialogue: 'Chào các bạn! Chào mừng đến với Voxel Anime Studio!',
          voice: { pitch: 1.2, rate: 1.0, gender: 'female' },
          camera: { preset: 'medium', smooth: true, shake: 0 }
        },
        {
          id: 'shot_2',
          title: 'Phân cảnh 2: Bác Ba mời trà',
          duration: 4.5,
          actorId: 'char_bac_ba',
          actorName: 'Bác Ba Quán Nước',
          spawnPoint: 'tea_stall_bench',
          action: 'talking',
          expression: 'happy',
          emotionFx: 'heart',
          dialogue: 'Cô giáo vào làm cốc trà đá mát lạnh cho đỡ nắng nhé!',
          voice: { pitch: 0.85, rate: 0.95, gender: 'male' },
          camera: { preset: 'close_up', smooth: true, shake: 0 }
        },
        {
          id: 'shot_3',
          title: 'Phân cảnh 3: Hứng khởi anime',
          duration: 3.5,
          actorId: 'char_teacher',
          actorName: 'Cô Giáo Lan',
          spawnPoint: 'sidewalk_center',
          action: 'cheer',
          expression: 'laugh',
          emotionFx: 'sweat',
          dialogue: 'Vâng ạ, trà đá vỉa hè Việt Nam là tuyệt vời nhất!',
          voice: { pitch: 1.2, rate: 1.0, gender: 'female' },
          camera: { preset: 'dutch_dramatic', smooth: true, shake: 0.1 }
        }
      ]
    };
  }

  /**
   * Lưu dự án thành file .vas.json trực tiếp vào ổ cứng máy tính
   * @param {object} projectData
   * @param {string} customFileName
   * @returns {Promise<{ success: boolean, fileName: string }>}
   */
  async saveProjectToFile(projectData, customFileName = null) {
    projectData.updatedAt = new Date().toISOString();
    const jsonStr = JSON.stringify(projectData, null, 2);
    const sanitizedTitle = (projectData.title || 'anime_project').toLowerCase().replace(/[^a-z0-9_-]/gi, '_');
    const fileName = customFileName || `${sanitizedTitle}.vas.json`;

    // 1. Thử nghiệm File System Access API hiện đại (Edge, Chrome, Opera, Brave)
    if (window.showSaveFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: fileName,
          types: [
            {
              description: 'Voxel Anime Studio Project (*.vas.json)',
              accept: { 'application/json': ['.vas.json', '.json'] }
            }
          ]
        });
        const writable = await handle.createWritable();
        await writable.write(jsonStr);
        await writable.close();
        this.fileHandle = handle;
        this.saveDraft(projectData);
        return { success: true, fileName: handle.name || fileName };
      } catch (err) {
        if (err.name === 'AbortError') {
          // Người dùng hủy hộp thoại lưu
          return { success: false, aborted: true };
        }
        console.warn('File System Access API không khả dụng hoặc bị từ chối, chuyển sang Fallback Download:', err);
      }
    }

    // 2. Fallback tải về (Browser Blob Download)
    try {
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      this.saveDraft(projectData);
      return { success: true, fileName };
    } catch (err) {
      console.error('Không thể lưu file dự án:', err);
      return { success: false, error: err.message };
    }
  }

  /**
   * Mở file dự án từ ổ cứng máy tính
   * @returns {Promise<{ success: boolean, project: object|null }>}
   */
  async openProjectFromFile() {
    // 1. Sử dụng File System Access API nếu hỗ trợ
    if (window.showOpenFilePicker) {
      try {
        const [handle] = await window.showOpenFilePicker({
          types: [
            {
              description: 'Voxel Anime Studio Project (*.vas.json)',
              accept: { 'application/json': ['.vas.json', '.json'] }
            }
          ],
          multiple: false
        });
        const file = await handle.getFile();
        const text = await file.text();
        const project = JSON.parse(text);
        this.fileHandle = handle;
        this.currentProject = project;
        this.saveDraft(project);
        return { success: true, project, fileName: file.name };
      } catch (err) {
        if (err.name === 'AbortError') {
          return { success: false, aborted: true };
        }
        console.warn('File System Access API thất bại, chuyển sang Fallback file input:', err);
      }
    }

    // 2. Fallback bằng phần tử input[type="file"]
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.vas.json,.json';
      input.style.display = 'none';

      input.onchange = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) {
          resolve({ success: false, aborted: true });
          return;
        }
        try {
          const text = await file.text();
          const project = JSON.parse(text);
          this.currentProject = project;
          this.saveDraft(project);
          resolve({ success: true, project, fileName: file.name });
        } catch (err) {
          console.error('Lỗi khi đọc file dự án:', err);
          resolve({ success: false, error: 'Định dạng file không hợp lệ.' });
        } finally {
          document.body.removeChild(input);
        }
      };

      input.oncancel = () => {
        resolve({ success: false, aborted: true });
        document.body.removeChild(input);
      };

      document.body.appendChild(input);
      input.click();
    });
  }

  /**
   * Lưu bản nháp dự án vào LocalStorage để phòng ngừa mất dữ liệu
   */
  saveDraft(projectData) {
    try {
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(projectData));
    } catch (err) {
      console.warn('Không thể lưu bản nháp dự án:', err);
    }
  }

  /**
   * Tải lại bản nháp dự án gần nhất
   */
  loadDraft() {
    try {
      const str = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (str) {
        return JSON.parse(str);
      }
    } catch (err) {
      console.warn('Không thể đọc bản nháp dự án:', err);
    }
    return null;
  }

  /**
   * Nén và mã hóa đối tượng project thành URL chứa hash #scene=...
   * @param {object} projectData
   * @param {string} [baseUrl]
   * @returns {string} URL chứa hash #scene=...
   */
  encodeProjectToUrl(projectData, baseUrl = null) {
    const origin = baseUrl || (typeof window !== 'undefined' ? window.location.origin + window.location.pathname : '');
    const jsonStr = JSON.stringify(projectData);
    let urlSafe = '';

    if (typeof Buffer !== 'undefined') {
      urlSafe = Buffer.from(jsonStr, 'utf-8').toString('base64url');
    } else {
      const utf8Bytes = new TextEncoder().encode(jsonStr);
      let binary = '';
      for (let i = 0; i < utf8Bytes.length; i++) {
        binary += String.fromCharCode(utf8Bytes[i]);
      }
      const base64 = btoa(binary);
      urlSafe = base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    }

    return `${origin}#scene=${urlSafe}`;
  }

  /**
   * Giải mã chuỗi URL hash hoặc query string thành đối tượng project
   * @param {string} [urlString]
   * @returns {object|null}
   */
  decodeProjectFromUrl(urlString = null) {
    try {
      const targetUrl = urlString || (typeof window !== 'undefined' ? window.location.href : '');
      let base64 = '';

      if (targetUrl.includes('#scene=')) {
        base64 = targetUrl.split('#scene=')[1].split('&')[0];
      } else if (targetUrl.includes('?scene=')) {
        const queryPart = targetUrl.split('?scene=')[1].split('#')[0].split('&')[0];
        base64 = decodeURIComponent(queryPart);
      }

      if (!base64) return null;

      let jsonStr = '';
      if (typeof Buffer !== 'undefined') {
        jsonStr = Buffer.from(base64, 'base64url').toString('utf-8');
      } else {
        let standardBase64 = base64.replace(/-/g, '+').replace(/_/g, '/');
        while (standardBase64.length % 4 !== 0) {
          standardBase64 += '=';
        }
        const binary = atob(standardBase64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }
        jsonStr = new TextDecoder().decode(bytes);
      }

      const project = JSON.parse(jsonStr);
      if (project && typeof project === 'object' && (project.title || project.storyboard || project.characters)) {
        return project;
      }
      return null;
    } catch (err) {
      console.warn('Lỗi khi giải mã dự án từ URL:', err);
      return null;
    }
  }
}
